import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Navbar } from '../components/Navbar';
import { AdminLayout } from '../components/AdminSidebar';
import { api, formatApiError } from '../lib/api';
import { toast } from 'sonner';

const PLAN_STATUS_OPTIONS = [
  ['', 'Any plan status'],
  ['active', 'Active'],
  ['unpaid', 'Unpaid'],
  ['paused', 'Paused'],
  ['cancelled', 'Cancelled'],
];
const QR_MODE_OPTIONS = [
  ['', 'Any QR mode'],
  ['public', 'Public'],
  ['private', 'Private'],
  ['family', 'Family'],
];
const PACKAGE_OPTIONS = [
  ['', 'Any package'],
  ['memorial', 'Memorial'],
  ['memorial_plus', 'Memorial Plus'],
  ['legacy', 'Legacy'],
];
const PLAQUE_OPTIONS = [
  ['', 'Any'],
  ['true', 'Has plaque order'],
  ['false', 'No plaque order'],
];

function shortDate(iso) { return iso ? iso.slice(0, 10) : '—'; }

export default function AdminMemorials() {
  const [q, setQ] = useState('');
  const [pkg, setPkg] = useState('');
  const [planStatus, setPlanStatus] = useState('');
  const [qrMode, setQrMode] = useState('');
  const [plaqueFilter, setPlaqueFilter] = useState('');
  const [includeTest, setIncludeTest] = useState(false);
  const [items, setItems] = useState([]);
  const [filteredTestCount, setFilteredTestCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (q.trim()) params.set('q', q.trim());
      if (pkg) params.set('package', pkg);
      if (planStatus) params.set('plan_status', planStatus);
      if (qrMode) params.set('qr_mode', qrMode);
      if (plaqueFilter) params.set('has_plaque_order', plaqueFilter);
      if (includeTest) params.set('include_test', 'true');
      const { data } = await api.get(`/admin/memorials?${params.toString()}`);
      setItems(data.memorials || []);
      setFilteredTestCount(data.filtered_test_count || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [q, pkg, planStatus, qrMode, plaqueFilter, includeTest]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <AdminLayout>
        <header className="mb-8">
          <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--accent))]">Platform admin</p>
          <h1 className="font-serif text-4xl text-[hsl(var(--primary))] mt-2" data-testid="admin-memorials-title">
            Memorials
          </h1>
          <p className="text-sm text-[hsl(var(--primary)/0.7)] mt-2 max-w-2xl">
            Find any memorial in the system by deceased name, slug, cemetery, or owner email. Click any row to open the full read-only dossier for support and plaque fulfillment.
          </p>
        </header>

        <div className="rounded-2xl border border-[hsl(var(--primary)/0.18)] bg-[hsl(var(--card))] p-5 mb-6 space-y-4">
          <input
            type="text"
            placeholder="Search deceased name, slug, cemetery, hometown, memorial ID…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full text-base bg-transparent border-b border-[hsl(var(--primary)/0.2)] focus:border-[hsl(var(--accent))] focus:outline-none px-2 py-2"
            data-testid="admin-memorials-search"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 text-xs">
            <FilterSelect label="Package" value={pkg} onChange={setPkg} options={PACKAGE_OPTIONS} testId="admin-memorials-package" />
            <FilterSelect label="Plan status" value={planStatus} onChange={setPlanStatus} options={PLAN_STATUS_OPTIONS} testId="admin-memorials-plan-status" />
            <FilterSelect label="QR mode" value={qrMode} onChange={setQrMode} options={QR_MODE_OPTIONS} testId="admin-memorials-qr-mode" />
            <FilterSelect label="Plaque order" value={plaqueFilter} onChange={setPlaqueFilter} options={PLAQUE_OPTIONS} testId="admin-memorials-plaque" />
            <label className="flex items-end gap-2 text-[hsl(var(--primary))]">
              <input
                type="checkbox"
                checked={includeTest}
                onChange={(e) => setIncludeTest(e.target.checked)}
                className="accent-[hsl(var(--accent))]"
                data-testid="admin-memorials-include-test"
              />
              <span className="text-xs">Show test data</span>
            </label>
          </div>
          {!includeTest && filteredTestCount > 0 && (
            <p className="text-[11px] text-[hsl(var(--primary)/0.55)]" data-testid="admin-memorials-test-hidden">
              Hidden: {filteredTestCount} synthetic / test memorials (Photo Tester, *@myleaflegacy.com, *@myleaftest.io).
            </p>
          )}
        </div>

        {loading ? (
          <div className="text-[hsl(var(--primary)/0.6)]" data-testid="admin-memorials-loading">Loading…</div>
        ) : items.length === 0 ? (
          <div className="rounded-2xl border border-[hsl(var(--primary)/0.18)] px-7 py-10 text-center text-[hsl(var(--primary)/0.6)]" data-testid="admin-memorials-empty">
            No memorials match these filters.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="admin-memorials-list">
            {items.map((m) => (
              <li
                key={m.memorial_id}
                data-testid={`admin-memorials-row-${m.memorial_id}`}
                className="rounded-xl border border-[hsl(var(--primary)/0.12)] bg-[hsl(var(--card))] hover:bg-[hsl(var(--primary)/0.03)] transition-colors"
              >
                <Link
                  to={`/admin/memorials/${m.memorial_id}`}
                  className="grid grid-cols-1 lg:grid-cols-12 gap-x-4 gap-y-2 px-5 py-4 items-center"
                  data-testid={`admin-memorials-open-${m.memorial_id}`}
                >
                  <div className="lg:col-span-3 min-w-0">
                    <div className="ml-overline lg:hidden">Memorial</div>
                    <div className="text-[hsl(var(--primary))] font-medium truncate">
                      {m.full_name || '—'}
                      {m.is_demo && <span className="ml-2 px-2 py-0.5 rounded-full bg-[hsl(var(--accent)/0.18)] text-[10px] uppercase tracking-[0.16em]">demo</span>}
                    </div>
                    <div className="text-[10px] text-[hsl(var(--primary)/0.55)] font-mono truncate">{m.slug}</div>
                  </div>
                  <div className="lg:col-span-3 min-w-0">
                    <div className="ml-overline lg:hidden">Owner</div>
                    <div className="text-[12px] text-[hsl(var(--primary)/0.8)] truncate">{m.owner?.name || '—'}</div>
                    <div className="text-[10px] text-[hsl(var(--primary)/0.55)] truncate">{m.owner?.email || '—'}</div>
                  </div>
                  <div className="lg:col-span-2 min-w-0">
                    <div className="ml-overline lg:hidden">Resting place</div>
                    <div className="text-[12px] text-[hsl(var(--primary)/0.8)] truncate">
                      {m.cemetery_name || m.hometown || '—'}
                    </div>
                    <div className="text-[10px] text-[hsl(var(--primary)/0.55)] truncate">
                      {[m.cemetery_city, m.cemetery_country].filter(Boolean).join(', ') || ''}
                    </div>
                  </div>
                  <div className="lg:col-span-2 min-w-0">
                    <div className="ml-overline lg:hidden">Plan / QR</div>
                    <div className="text-[11px] text-[hsl(var(--primary)/0.8)] capitalize">
                      {m.package_id || '—'} · {m.plan_status || 'active'}
                    </div>
                    <div className="text-[10px] text-[hsl(var(--primary)/0.55)]">
                      QR · {m.qr_access_mode || 'public'}
                    </div>
                  </div>
                  <div className="lg:col-span-2 flex items-center justify-between lg:justify-end gap-3">
                    <div>
                      <div className="ml-overline lg:hidden">Plaque</div>
                      <div className="text-[11px] text-[hsl(var(--primary)/0.8)] tabular-nums">
                        {(m.plaque_orders_summary?.total || 0) > 0
                          ? `${m.plaque_orders_summary.total} order${m.plaque_orders_summary.total === 1 ? '' : 's'}${m.plaque_orders_summary.shipped ? ` · ${m.plaque_orders_summary.shipped} shipped` : ''}`
                          : '—'}
                      </div>
                      <div className="text-[10px] text-[hsl(var(--primary)/0.55)] tabular-nums">
                        {shortDate(m.created_at)}
                      </div>
                    </div>
                    <span aria-hidden className="text-[hsl(var(--accent))] text-[12px] font-semibold tracking-[0.18em] shrink-0">
                      OPEN →
                    </span>
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </AdminLayout>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="uppercase tracking-[0.18em] text-[10px] text-[hsl(var(--primary)/0.55)] font-medium">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-transparent border border-[hsl(var(--primary)/0.18)] rounded px-2 py-1.5 text-[hsl(var(--primary))]"
        data-testid={testId}
      >
        {options.map(([val, lab]) => (<option key={val} value={val}>{lab}</option>))}
      </select>
    </label>
  );
}
